import { useForm } from "react-hook-form";
import { FileUploadShapefile } from "../../../../components/EditOnboarding/components/FileUploadShapefile";
import { Dropdown } from "../../../../components/EditOnboarding/components/Select";
import axios from "../../../../services/axios";
import { useEffect, useRef, useState } from "react";
import { IOptionsProps, IOptionProps } from "../../../../@types/reactSelect/IOption";
import { IAnalysesShapeFile } from "../../../../@types/Onboarding/Shapefile/IAnalyses";
import Constants from "../../../../constants/Index";
import { IProcessShapefile } from "../../../../@types/Onboarding/Shapefile/IProcess";
import { FooterControlls } from "../../../../components/Onboarding/Template/FooterControlls/Index";
import { useOnboarding } from "../../../../hooks/useOnboarding";
import { ButtonOnlyTitle } from "../../../../components/Buttons/ButtonOnlyTitle/Index";
import { Loading } from "../../../../components/Loading/Index";
import * as yup from 'yup'
import { yupResolver } from "@hookform/resolvers/yup";
import { AlertModal } from "../../../../components/Modal/Alert/Index";
import { TitleModal } from "../../../../components/Modal/Contents/TitleModal/Index";
import { ButtonRedirect } from "../../../../components/Modal/Error/ButtonRedirect/Index";

const shapeFileForm = yup.object().shape({
    farmAttribute: yup
        .object({
            label: yup.string(),
            value: yup
                .string()
                .required('É necessário indicar o atributo de fazenda.'),
        })
        .required('É necessário indicar o atributo de fazenda.')
        .nullable(),
    areaAttribute: yup
        .object({
            label: yup.string(),
            value: yup
                .string()
                .required('É necessário indicar o atributo de retiro.'),
        })
        .required('É necessário indicar o atributo de retiro.')
        .nullable(),
    microAreaAttribute: yup
        .object({
            label: yup.string(),
            value: yup
                .string()
                .required('É  necessário indicar o atributo de pasto.'),
        })
        .required('É necessário indicar o atributo de pasto.')
        .nullable(),
    areaHAAttribute: yup
        .object({
            label: yup.string(),
            value: yup
                .string()
                .required('É necessário indicar o atributo de área.'),
        })
        .required('É necessário indicar o atributo de área.')
        .nullable(),
})

export function OnboardingShapeFile() {
    const {
        handleSubmit,
        register,
        setValue,
        control,
        watch,
        formState: { errors },
        getFieldState
    } = useForm({
        resolver: yupResolver(shapeFileForm),
    });

    const [optionsAttributes, setOptionsAttributes] = useState<IOptionsProps>([]);
    const [shapeFile, setShapeFile] = useState<FormData>();
    const { setPayload, setBlockNextStep } = useOnboarding();
    const [onBoardingWithShapeFile, setOnBoardingWithShapeFile] = useState(false);

    const handleDropdownSelection = (currentDropdown: string, currentOption: IOptionProps) => {
        const dropdowns = [
            'farmAttribute',
            'areaAttribute',
            'microAreaAttribute',
            'areaHAAttribute'
        ] as const;

        dropdowns.forEach(dropdown => {
            const otherValue = watch(dropdown);
            // Reset value if duplicated
            if (dropdown !== currentDropdown && otherValue?.value == currentOption.value) {
                setValue(dropdown, null);
                setBlockNextStep(true)
                return;
            }
        });

        setBlockNextStep(false)
    };

    async function analyzeShapeFile(data: any) {
        try {
            const formData = new FormData();
            formData.append("file", data.target.files[0]);

            setShapeFile(formData)
            await axios.post('shapefile/analyze', formData).then((response) => {
                const analyses = (response.data as IAnalysesShapeFile)
                setOptionsAttributes(analyses.attributes.map((attribute) => ({
                    label: attribute,
                    value: attribute,
                })));

                const attributeFields = [
                    'farmAttribute',
                    'areaAttribute',
                    'microAreaAttribute',
                    'areaHAAttribute'
                ] as const;

                attributeFields.forEach(field => {
                    const suggestion = analyses.suggestions[field];
                    if (suggestion) {
                        setValue(field, {
                            label: suggestion,
                            value: suggestion
                        });
                    }
                });
            });

        } catch (error) {
            console.error('Error processing shapefile:', error);
            setInvalidShapeFile(true)
        }
    }

    const transformPolygonToLatLng = (multiPolygon: number[][][]) => {
        return multiPolygon.map(areas =>
            areas.map(area => ({
                "lng": area[0],
                "lat": area[1]
            })
            ))
    };

    const [visibleLoading, setVisibleLoading] = useState<boolean>(false);

    async function validateForm() {
        handleSubmit(() => { })();

        const result = await submitForm();
        return result ?? false;
    }

    const [invalidShapeFile, setInvalidShapeFile] = useState(false);

    async function submitForm() {
        if (!onBoardingWithShapeFile) return true;

        try {
            const selectedAttributes = {
                farmAttribute: watch('farmAttribute')?.value,
                areaAttribute: watch('areaAttribute')?.value,
                microAreaAttribute: watch('microAreaAttribute')?.value,
                areaHAAttribute: watch('areaHAAttribute')?.value
            };

            if (Object.values(selectedAttributes).some(value => value === undefined)) {
                return false;
            }

            const params = new URLSearchParams();
            Object.entries(selectedAttributes).forEach(([key, value]) => {
                if (value) {
                    params.append(key, value);
                }
            });

            setVisibleLoading(true);

            await axios.post(`shapefile/polygon/process?${params.toString()}`, shapeFile)
                .then(({ data }) => {
                    let payloadResponse = {} as any;
                    payloadResponse.farms = (data as IProcessShapefile).farms.map((farm, index) => ({
                        uuid: `farm-${index}`,
                        isNew: "true",
                        name: farm.name,
                        initials: farm.initials,
                        document: null,
                        state_registration: null,
                        address: {
                            city: {
                                code: null,
                                name: null
                            },
                            state: null,
                            street: null
                        },
                        manager_user_id: null,
                        properties: {
                            season: {
                                dry_initial: 3,
                                rain_initial: 8
                            }
                        },
                        file: {
                            coords: transformPolygonToLatLng(farm.geometry.coordinates).flat(),
                            name: farm.name
                        },
                        map_coords: transformPolygonToLatLng(farm.geometry.coordinates).flat(),
                        filename: null
                    })
                    );

                    payloadResponse.areas = (data as IProcessShapefile).farms.map((farm, farmId) =>
                        farm.areas.map((area, areaId) => ({
                            uuid: `${farmId}-area-${areaId}`,
                            isNew: "true",
                            farm_id: `farm-${farmId}`,
                            name: area.name,
                            acreage: `${Number(area.area).toFixed(2)}`,
                            initials: area.initials,
                            indoor_grazing_time: null,
                            outdoor_grazing_time: null,
                            manager_user_id: null,
                            map_coords: {
                                uuid: `${farmId}-area-${areaId}`,
                                coords: transformPolygonToLatLng(area.geometry.coordinates).flat()
                            }
                        }))
                    ).flat();

                    payloadResponse.micro_areas = (data as IProcessShapefile).farms.map((farm, farmId) => farm.areas.map((area, areaId) => area.micro_areas.map((microArea, microAreaId) => ({
                        isNew: "true",
                        area_id: `${farmId}-area-${areaId}`,
                        uuid: `${farmId}-microArea-${microAreaId}`,
                        initials: microArea.name,
                        acreage: `${Number(microArea.area).toFixed(2)}`,
                        map_coords: {
                            uuid: `${farmId}-microArea-${microAreaId}`,
                            coords: transformPolygonToLatLng(microArea.geometry.coordinates).flat()
                        }
                    })))).flat().flat()

                    setPayload(() => {
                        localStorage.setItem(Constants.LOCALSTORAGE_PAYLOAD, JSON.stringify(payloadResponse));
                        return payloadResponse;
                    });
                })
                .finally(() => setVisibleLoading(false));
            return true;
        } catch (error) {
            console.error('Error processing shapefile:', error);
            setInvalidShapeFile(true)
            return false;
        }
    }

    useEffect(() => {
        setBlockNextStep(false);
    }, [onBoardingWithShapeFile]);

    return <>
        <Loading visible={visibleLoading} />

        {invalidShapeFile && (
            <AlertModal
                visible={invalidShapeFile}
                contentStyle={{
                    width: '490px',
                    height: '140px',
                    borderRadius: '20px',
                }}
            >
                <section className="modalEdit__alert">
                    <TitleModal>ShapeFile inválido</TitleModal>
                    <ButtonRedirect
                        onClick={() => {
                            setInvalidShapeFile(false);
                        }}
                    >
                        Tentar novamente
                    </ButtonRedirect>
                </section>
            </AlertModal>
        )}

        <main className="createArea__container" style={{ padding: 0, display: "flex", flexDirection: "column", gap: 20 }}>
            <div className="createassortment__container--input">
                <h1 id="onboarding-title-page">Onboarding</h1>
                <label htmlFor="createassortment__title">Você deseja realizar o onboarding manualmente ou através do shapefile?</label>
                <div style={{ display: "flex", gap: 50 }}>
                    <ButtonOnlyTitle
                        title={"Manual"}
                        theme={!onBoardingWithShapeFile ? "info" : "light"}
                        onClick={() => setOnBoardingWithShapeFile(false)}
                    />
                    <ButtonOnlyTitle
                        title={"ShapeFile"}
                        theme={onBoardingWithShapeFile ? "info" : "light"}
                        onClick={() => setOnBoardingWithShapeFile(true)}
                    />
                </div>
            </div>


            <form style={{ width: 500 }} hidden={!onBoardingWithShapeFile} onSubmit={handleSubmit(submitForm)}>

                <FileUploadShapefile name={"shapefile"} control={control} onSubmit={analyzeShapeFile} />

                <Dropdown
                    label={"Selecione o atributo da Fazenda"}
                    control={control}
                    closeMenuOnSelect={true}
                    placeholderText={"Selecione..."}
                    {...register("farmAttribute")}
                    options={optionsAttributes}
                    getSelectedData={(value) => handleDropdownSelection('farmAttribute', value)}
                    errorText={(errors.farmAttribute as any)?.value?.message}
                />

                <Dropdown
                    label={"Selecione o atributo do Retiro"}
                    control={control}
                    closeMenuOnSelect={true}
                    placeholderText={"Selecione..."}
                    {...register("areaAttribute")}
                    options={optionsAttributes}
                    getSelectedData={(value) => handleDropdownSelection('areaAttribute', value)}
                    errorText={(errors.areaAttribute as any)?.value?.message}
                />

                <Dropdown
                    label={"Selecione o atributo do Pasto"}
                    control={control}
                    closeMenuOnSelect={true}
                    placeholderText={"Selecione..."}
                    {...register("microAreaAttribute")}
                    options={optionsAttributes}
                    getSelectedData={(value) => handleDropdownSelection('microAreaAttribute', value)}
                    errorText={(errors.microAreaAttribute as any)?.value?.message}
                />

                <Dropdown
                    label={"Selecione o atributo da unidade de área"}
                    control={control}
                    closeMenuOnSelect={true}
                    placeholderText={"Selecione..."}
                    {...register("areaHAAttribute")}
                    options={optionsAttributes}
                    getSelectedData={(value) => handleDropdownSelection('areaHAAttribute', value)}
                    errorText={(errors.areaHAAttribute as any)?.value?.message}
                />
            </form>
        </main>

        <footer>
            <FooterControlls nextStepEvent={validateForm} />
        </footer>
    </>
}